//
// # Editor viewport control
//
// Owns the Editor's viewport reveal behavior: panning to center a bbox with an
// animated ease-in-out tween (used e.g. by SRV Results-row selection). The pure
// transform helpers (recenterTransform, easeInOutCubic, lerpTransform) are kept
// module-level and unit-testable without constructing a canvas.
//

import type { Canvas } from "EditorCore/src/canvas";
import { WorldTransform, WorldTransforms } from "DrawCore/src/transforms";

interface MinMaxBbox {
  min: { x: number; y: number };
  max: { x: number; y: number };
}

// Returns the schToPage transform that pans (keeping the current scale) to
// center `bbox` in the viewport. Always recenters, whether or not the bbox is
// already visible. Returns null for a zero-size canvas (e.g. a tab hidden by
// dockview), where centering is undefined.
export function recenterTransform(
  bbox: MinMaxBbox,
  size: { width: number; height: number },
  transforms: WorldTransforms
): WorldTransform | null {
  const { width, height } = size;
  if (width === 0 || height === 0) {
    return null;
  }

  // Re-center on the bbox center at the current scale (no zoom). The offset
  // convention matches fitToBbox (page y grows downward, world y upward, hence
  // the +scale*cy term).
  const { min, max } = bbox;
  const { scale } = transforms.schToPage;
  const cx = (min.x + max.x) / 2;
  const cy = (min.y + max.y) / 2;
  return {
    scale,
    offset: {
      x: width / 2 - scale * cx,
      y: height / 2 + scale * cy,
    },
  };
}

// Standard cubic ease-in-out: slow at both ends, fast in the middle. `t` is the
// normalized progress in [0, 1]; the return value is the eased progress in
// [0, 1].
export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// Linearly interpolate between two transforms by `t` in [0, 1]. Used to animate
// a viewport pan/zoom frame-by-frame between a start and target transform.
export function lerpTransform(
  from: WorldTransform,
  to: WorldTransform,
  t: number
): WorldTransform {
  return {
    scale: from.scale + (to.scale - from.scale) * t,
    offset: {
      x: from.offset.x + (to.offset.x - from.offset.x) * t,
      y: from.offset.y + (to.offset.y - from.offset.y) * t,
    },
  };
}

// Duration of the centerOnBbox pan animation, in milliseconds.
const CENTER_ANIMATION_MS = 200;

// Viewport control for an Editor's canvas. Holds the transient animation state
// for an in-flight pan so successive reveals don't fight each other.
export class EditorViewport {
  // Handle for the in-flight centerOnBbox pan animation, so a new reveal can
  // cancel the previous one rather than fighting it frame-by-frame.
  private centerAnimationId: number | null = null;

  constructor(private readonly canvas: Canvas) {}

  // Pan the viewport (without changing zoom) so `bbox` is centered. Used by SRV
  // Results-row selection to reveal a violation arrow without disrupting the
  // user's zoom level. Animates the pan with a 300ms ease-in-out tween rather
  // than jumping instantly.
  centerOnBbox = (bbox: {
    min: { x: number; y: number };
    max: { x: number; y: number };
  }) => {
    const target = recenterTransform(
      bbox,
      this.canvas.size,
      this.canvas.worldTransforms
    );
    if (!target) return;

    // Cancel any pan already in flight so we animate from the current position.
    if (this.centerAnimationId !== null) {
      cancelAnimationFrame(this.centerAnimationId);
      this.centerAnimationId = null;
    }

    const from = {
      scale: this.canvas.worldTransforms.schToPage.scale,
      offset: { ...this.canvas.worldTransforms.schToPage.offset },
    };
    const startTime = performance.now();

    const step = () => {
      const t = Math.min(
        (performance.now() - startTime) / CENTER_ANIMATION_MS,
        1
      );
      const eased = easeInOutCubic(t);
      this.canvas.updateWorldTransforms(
        lerpTransform(from, target, eased),
        "schToPage"
      );
      if (t < 1) {
        this.centerAnimationId = requestAnimationFrame(step);
      } else {
        this.centerAnimationId = null;
      }
    };
    this.centerAnimationId = requestAnimationFrame(step);
  };
}
